import { payoutsQueryDocument } from 'APIs/graphql';
import { PayoutStatus } from 'APIs/graphql/generated/graphql';
import { reversePayout } from 'APIs/payouts/payouts';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'urql';
import getErrorMessage from 'utils/getErrorMessage';

import { FilterOptions } from '../styles';
import PayoutDataTable from './PayoutDataTable';
import { IListingPagePropsObj } from './types';

const initListingPageProps = { first: 20, last: null, before: null, after: null, page: 1 };
const LISTING_RECORDS_PER_PAGE = 20;

type Props = {
  merchantId?: string;
};

const FailedPayouts = ({ merchantId }: Props) => {
  const { apiBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
  }));

  const [includeReversed, setIncludeReversed] = useState<boolean>(false);
  const [listingPageProps, setListingPagePropsObj] = useState<IListingPagePropsObj>(initListingPageProps);

  const { first, last, before, after } = useMemo(() => listingPageProps, [listingPageProps]);

  const limit = useMemo(() => first ?? last ?? LISTING_RECORDS_PER_PAGE, [first, last]);

  const filters = useMemo(() => {
    return {
      status: (includeReversed ? ['Failed', 'Reversed'] : ['Failed']) as PayoutStatus[],
      merchantIds: merchantId ? [merchantId] : undefined,
    };
  }, [includeReversed, merchantId]);

  const [{ fetching, data, error: payoutsError }, reexecuteQuery] = useQuery({
    query: payoutsQueryDocument,
    variables: { last, before, first, after, ...filters },
  });

  const handlePageChange = useCallback(
    (page: number) => {
      if (!data?.payouts) return;

      const isFirst = page === 1;
      const isPrev = !isFirst && page === listingPageProps.page - 1;
      const isNext = page === listingPageProps.page + 1;
      const isLast = !isFirst && !isNext && page === Math.ceil(data.payouts.totalCount / limit);

      setListingPagePropsObj({
        ...listingPageProps,
        page,
        last: isLast || isPrev ? limit : null,
        before: !isLast && isPrev ? data.payouts.pageInfo.startCursor ?? null : null,
        first: isFirst || isNext ? limit : null,
        after: !isFirst && isNext ? data.payouts.pageInfo.endCursor ?? null : null,
      });
    },
    [data?.payouts, limit, listingPageProps],
  );

  const handlePerRowsChange = useCallback(
    async (newPerPage: number, page: number) => {
      setListingPagePropsObj({
        ...listingPageProps,
        page,
        last: listingPageProps.last !== null ? newPerPage : null,
        first: listingPageProps.first !== null ? newPerPage : null,
      });
    },
    [listingPageProps],
  );

  const reversePayoutAction = useCallback(
    async (merchantId: string, payoutId: string) => {
      if (!payoutId) return;

      if (window.confirm(`Are you sure you want to reverse payout: ${payoutId}`)) {
        try {
          await reversePayout(apiBaseUri, merchantId, payoutId);
          reexecuteQuery({ requestPolicy: 'network-only' });
        } catch (e) {
          console.log(getErrorMessage(e));
        }
      }
    },
    [apiBaseUri, reexecuteQuery],
  );

  return (
    <div style={{ width: '100%' }}>
      <h3>Failed payouts</h3>
      <FilterOptions>
        <input
          type="checkbox"
          id="incl-reversed-checkbox"
          checked={includeReversed}
          onChange={(e) => setIncludeReversed(e.target.checked)}
          data-testid="incl-reversed-checkbox"
        />
        <label htmlFor="incl-reversed-checkbox" className="ml-8">
          Include Reversed payouts
        </label>
        <br />
      </FilterOptions>
      <PayoutDataTable
        payoutList={data?.payouts?.edges}
        totalRows={data?.payouts?.totalCount ?? 0}
        handlePageChange={handlePageChange}
        handlePerRowsChange={handlePerRowsChange}
        fetchListLoader={fetching}
        limit={limit}
        isErrorRequest={payoutsError !== undefined}
        reversePayout={reversePayoutAction}
        hideMerchantColumn={!!merchantId}
      />
    </div>
  );
};

export default FailedPayouts;
